import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Seo, breadcrumbLd, ORIGIN } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useAuth } from "@/context/AuthContext";
import { productUrl } from "@/lib/productUrl";
import { toast } from "sonner";
import { SlidersHorizontal, X, ChevronDown, Check } from "lucide-react";
import { mpAddToWishlist } from "@/lib/metaPixel";

const MULTI = [
  ["category", "Category"],
  ["occasion", "Occasion"],
  ["color", "Color"],
  ["size", "Size"],
  ["silhouette", "Silhouette"],
  ["neckline", "Neckline"],
  ["length", "Length"],
  ["sleeve", "Sleeve"],
  ["designer", "Designer"],
];
const SORTS = [["", "Featured"], ["newest", "New Arrivals"], ["trending", "Trending"], ["price_asc", "Price ↑"], ["price_desc", "Price ↓"]];
const LIMIT = 24;

export default function Shop() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [facets, setFacets] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [favs, setFavs] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  const [sel, setSel] = useState(() => {
    const init = {};
    MULTI.forEach(([k]) => (init[k] = []));
    if (params.get("occasion")) init.occasion = [params.get("occasion")];
    if (params.get("category")) init.category = [params.get("category")];
    return init;
  });
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [newArrival, setNewArrival] = useState(false);
  const [trending, setTrending] = useState(false);
  const [availableOnly, setAvailableOnly] = useState(false);
  const [sort, setSort] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => { api.globalPriceFacets().then(({ data }) => setFacets(data)); }, []);
  useEffect(() => { if (user) api.favorites().then(({ data }) => setFavs(data.map((p) => p.id))); }, [user]);

  const buildParams = useCallback((skipVal) => {
    const p = { paginate: true, skip: skipVal, limit: LIMIT };
    MULTI.forEach(([k]) => { if (sel[k].length) p[k] = sel[k].join(","); });
    if (priceMin) p.price_min = priceMin;
    if (priceMax) p.price_max = priceMax;
    if (newArrival) p.new_arrival = true;
    if (trending) p.trending = true;
    if (availableOnly) p.available_only = true;
    if (sort) p.sort = sort;
    if (q) p.q = q;
    return p;
  }, [sel, priceMin, priceMax, newArrival, trending, availableOnly, sort, q]);

  const load = useCallback(() => {
    api.products(buildParams(0)).then(({ data }) => { setItems(data.items); setTotal(data.total); setSkip(0); });
  }, [buildParams]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const loadMore = () => {
    const next = skip + LIMIT;
    api.products(buildParams(next)).then(({ data }) => { setItems((prev) => [...prev, ...data.items]); setSkip(next); });
  };

  const toggle = (key, val) => setSel((s) => ({ ...s, [key]: s[key].includes(val) ? s[key].filter((x) => x !== val) : [...s[key], val] }));
  const clearAll = () => {
    const cleared = {}; MULTI.forEach(([k]) => (cleared[k] = []));
    setSel(cleared); setPriceMin(""); setPriceMax(""); setNewArrival(false); setTrending(false); setAvailableOnly(false); setQ("");
  };
  const activeCount = MULTI.reduce((a, [k]) => a + sel[k].length, 0) + (priceMin || priceMax ? 1 : 0) + (newArrival ? 1 : 0) + (trending ? 1 : 0) + (availableOnly ? 1 : 0);

  const onFav = async (pid) => {
    if (!user) { toast("Sign in to save favorites"); return; }
    const { data } = await api.toggleFavorite(pid);
    setFavs(data.favorites);
    toast(data.added ? "Saved to favorites" : "Removed");
    // Meta Pixel — AddToWishlist (only on ADD)
    if (data.added) {
      try {
        const prod = items.find((p) => p.id === pid);
        if (prod) mpAddToWishlist(prod);
      } catch { /* noop */ }
    }
  };

  const heading = sel.occasion.length === 1 ? sel.occasion[0][0].toUpperCase() + sel.occasion[0].slice(1) : "All Looks";
  const crumbs = [{ name: "Home", path: "/" }, { name: "Shop", path: "/shop" }];

  const Sidebar = () => (
    <div className="space-y-1">
      <div className="flex items-center justify-between mb-4">
        <span className="font-label text-[10px]">Filters {activeCount > 0 && `(${activeCount})`}</span>
        {activeCount > 0 && <button data-testid="clear-filters" onClick={clearAll} className="font-label text-[9px] text-rosemetal">Clear all</button>}
      </div>
      {user?.school_verified && (
        <label className="flex items-center gap-2 py-3 border-b border-border cursor-pointer" data-testid="available-toggle">
          <input type="checkbox" checked={availableOnly} onChange={(e) => setAvailableOnly(e.target.checked)} className="accent-rosemetal" />
          <span className="font-body text-sm">Available at my school</span>
        </label>
      )}
      <label className="flex items-center gap-2 py-3 border-b border-border cursor-pointer">
        <input type="checkbox" checked={newArrival} onChange={(e) => setNewArrival(e.target.checked)} className="accent-rosemetal" />
        <span className="font-body text-sm">New arrivals</span>
      </label>
      <label className="flex items-center gap-2 py-3 border-b border-border cursor-pointer">
        <input type="checkbox" checked={trending} onChange={(e) => setTrending(e.target.checked)} className="accent-rosemetal" />
        <span className="font-body text-sm">Trending</span>
      </label>
      {MULTI.map(([key, label]) => (
        <FacetGroup key={key} label={label} options={facets?.[key] || []} selected={sel[key]} onToggle={(v) => toggle(key, v)} testid={`facet-${key}`} />
      ))}
      <div className="py-4 border-b border-border">
        <div className="font-body text-sm mb-3">Price {facets && `($${facets.price_min}–$${facets.price_max})`}</div>
        <div className="flex items-center gap-2">
          <input data-testid="price-min" type="number" placeholder="Min" value={priceMin} onChange={(e) => setPriceMin(e.target.value)} className="w-full border border-border rounded-sm px-2 py-1.5 text-sm outline-none focus:border-rosemetal" />
          <span className="text-muted-foreground">–</span>
          <input data-testid="price-max" type="number" placeholder="Max" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} className="w-full border border-border rounded-sm px-2 py-1.5 text-sm outline-none focus:border-rosemetal" />
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="shop-page" className="max-w-[1500px] mx-auto px-5 sm:px-8 pt-12">
      <Seo
        title={sel.occasion.length === 1 ? `${heading} Dresses in Wisconsin` : "Shop All Formalwear"}
        description={`Browse ${heading.toLowerCase()} formalwear — filter by color, size, silhouette, designer & price. Lock your look for your school.`}
        path={sel.occasion.length === 1 ? `/shop?occasion=${sel.occasion[0]}` : "/shop"}
        jsonLd={[breadcrumbLd(crumbs), { "@context": "https://schema.org", "@type": "ItemList", numberOfItems: total, itemListElement: items.slice(0, 20).map((p, i) => ({ "@type": "ListItem", position: i + 1, url: `${ORIGIN}${productUrl(p)}`, name: p.name })) }]}
      />
      <Breadcrumbs items={crumbs} />
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <p className="font-label text-[10px] text-rosemetal mb-2">The Collection</p>
          <h1 className="font-display text-5xl sm:text-6xl">{heading}</h1>
        </div>
        <div className="flex items-center gap-3">
          <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search dresses, designers…" className="bg-transparent border-b border-border py-1.5 text-sm outline-none focus:border-rosemetal w-44" />
          <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="bg-transparent font-label text-[10px] outline-none cursor-pointer border border-border rounded-sm px-3 py-2">
            {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button data-testid="mobile-filter-btn" onClick={() => setMobileOpen(true)} className="lg:hidden flex items-center gap-2 border border-ink px-4 py-2 rounded-sm font-label text-[10px]">
            <SlidersHorizontal className="w-3.5 h-3.5" /> Filters{activeCount > 0 && ` (${activeCount})`}
          </button>
        </div>
      </div>

      <p className="font-body text-xs text-muted-foreground mb-8" data-testid="result-count">{total} {total === 1 ? "look" : "looks"}</p>

      <div className="grid lg:grid-cols-[240px_1fr] gap-10">
        <aside className="hidden lg:block sticky top-[90px] self-start max-h-[calc(100vh-110px)] overflow-y-auto pr-2">
          <Sidebar />
        </aside>

        <div>
          {items.length === 0 ? (
            <p className="font-body text-muted-foreground py-20 text-center">No looks match — try widening your filters.</p>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-5 gap-y-12">
                {items.map((p, i) => <ProductCard key={p.id} product={p} index={i % LIMIT} isFav={favs.includes(p.id)} onFav={onFav} focusColor={sel.color.length === 1 ? sel.color[0] : null} />)}
              </div>
              {items.length < total && (
                <div className="flex justify-center mt-16">
                  <button data-testid="load-more-btn" onClick={loadMore} className="border border-ink px-10 py-3.5 font-label text-[10px] rounded-sm hover:bg-ink hover:text-ivory transition-colors">
                    Load more ({total - items.length} left)
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* mobile filter drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[70] lg:hidden" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0 bg-ink/50" />
          <div className="absolute right-0 top-0 h-full w-[85vw] max-w-sm bg-ivory p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <span className="font-display text-2xl">Filters</span>
              <button onClick={() => setMobileOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <Sidebar />
            <button onClick={() => setMobileOpen(false)} className="w-full mt-6 bg-ink text-ivory py-3.5 font-label text-[10px] rounded-sm">Show {total} looks</button>
          </div>
        </div>
      )}
    </div>
  );
}

function FacetGroup({ label, options, selected, onToggle, testid }) {
  const [open, setOpen] = useState(true);
  if (!options.length) return null;
  return (
    <div className="py-4 border-b border-border" data-testid={testid}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between">
        <span className="font-body text-sm">{label}{selected.length > 0 && <span className="text-rosemetal"> ({selected.length})</span>}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-2 max-h-52 overflow-y-auto">
          {options.map((o) => (
            <label key={o} className="flex items-center gap-2 cursor-pointer group">
              <span className={`w-4 h-4 border rounded-sm flex items-center justify-center transition-colors ${selected.includes(o) ? "bg-ink border-ink" : "border-border group-hover:border-ink"}`}>
                {selected.includes(o) && <Check className="w-3 h-3 text-ivory" />}
              </span>
              <input type="checkbox" checked={selected.includes(o)} onChange={() => onToggle(o)} className="sr-only" data-testid={`${testid}-${o}`} />
              <span className="font-body text-sm capitalize">{o}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
